import { useMemo } from "react";
import { getFragmentData } from "@/__generated__";
import type {
  JobScheduleTypeEnum,
  PackageDetailFragmentFragment,
  VisitClientFragmentFragment,
} from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageDetailFragmentFragmentDoc,
  PackageServiceTypeEnum,
  RebookActionFieldsFragmentDoc,
  VisitStatusEnum,
} from "@/__generated__/graphql";
import { usePathname } from "@/components/hooks/use-pathname";
import { CaretRight, ClipboardText, Receipt, WarningCircle } from "@/components/shared/icons";
import { Button, Skeleton, View } from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import { AddToCalendarButton } from "@/containers/booking-complete/add-to-calendar";
import type { ServiceProfileItem } from "@/containers/profile/service-profile/lib";
import { mapPackageClientProfileToItem } from "@/containers/visits/lib";
import { isReadyToSchedule } from "@/lib/booking-lib";
import { onChatSales } from "@/lib/chat-sales";
import { copyClipboard } from "@/lib/helpers/copy-clipboard";
import { formatDate } from "@/lib/helpers/date";
import { formatPrice, SINGAPORE_TAX, singaporeTaxCalculation } from "@/lib/helpers/number";
import { getLineItemClientDetail } from "@/lib/line-items/helpers";
import { handleWhatsappNumber } from "@/lib/platform";
import { getWorkerLabel, packageServiceTypeLabel } from "@/lib/service/helpers";
import { formatDecimalHour } from "@/lib/utils";
import { useVisitDetailStore } from "@/store/visits/visitDetail";
import type { LineItemDetail } from "@/types/line-item";
import { OfflineBookingServiceTypes, OnlineBookingDepartments } from "@/types/service";
import { addMonths } from "date-fns";

import { ServiceReportCard } from "@luce/customer-app";

import { Divider } from "../../divider";
import { IfElse } from "../../if-else";
import { LineItemList } from "../../line-item/line-item-list";
import { ListHeading } from "../../list-header";
import { ServiceProfileCard } from "../../service-profile/service-profile-card";
import { Typography } from "../../typography";
import {
  formatVisitAddress,
  formatVisitSchedule,
  serviceProfileMapping,
  visitWorkerFullName,
} from "../utils";
import type { VisitData } from "../visit-card";
import { isVisitStatusPending, visitShowsRebookButton } from "../visit-card";
import { VisitRating } from "../visit-card/visit-rating";
import { VisitStatusBadge } from "../visit-card/visit-status-badge";
import { WorkerAvatar } from "../visit-card/worker-avatar";
import { ActionButton } from "../visit-detail-modal/action-button";

export type VisitDetailData = VisitData & {
  clientName: string;
  postalCode: string;
  ratingComment: string;
  reviewEditable: boolean;
  reviewAttachments: { id: string; url: string }[];
  lineItems: LineItemDetail[];
  token: string | null;
  reportDate: Date | null;
  clientCanReschedule: boolean;
  packageProfile: ServiceProfileItem | null;
  modificationLink: string | null;
};

export type VisitDetailHandler = {
  handleReschedule(id: string): void;
  handleChangeWorker(id: string): void;
  handleReportVisit(id: string): void;
  handleSkipVisit(id: string): void;
  handleRateVisit(id: string, rate?: number): void;
  handleRebook?(visitData: VisitDetailData): void;
};

type Props = {
  visitData: VisitDetailData | null;
  loading: boolean;
} & VisitDetailHandler;

export function mapToVisitDetailData(
  visit: VisitClientFragmentFragment,
  token: string | null = null,
): VisitDetailData {
  const pkg = visit.job?.package as NonNullable<VisitClientFragmentFragment["job"]>["package"];

  let value = 0;

  let packageProfile: ServiceProfileItem | null = null;
  if (visit.packageClientProfile) {
    packageProfile = mapPackageClientProfileToItem(visit.packageClientProfile, pkg.department);
  }

  const lineItems = visit.visitLineItems.map((item) => {
    value += item.billingValue;
    return getLineItemClientDetail(
      getFragmentData(PackageDetailFragmentFragmentDoc, item.packageDetail),
      {
        billingValue: item.billingValue,
        unitValue: item.unitValue,
        description: item.packageDescription,
        unitNumber: item.units,
      },
    );
  });

  const packageDetail = visit.visitLineItems[0].packageDetail as PackageDetailFragmentFragment;
  const canReschedule = packageDetail.clientCanReschedule && packageDetail.numberOfWorkers === 1;

  return {
    id: visit.id,
    clientName: visit.clientName,
    department: pkg.department,
    serviceType: pkg.serviceType,
    status: visit.status,
    value,
    serviceDate: new Date(visit.serviceDate),
    startTime: String(visit.startTime),
    endTime: String(visit.endTime),
    address: formatVisitAddress(pkg.fullAddress, pkg.unitNumber ?? "", pkg.postalCode),
    postalCode: pkg.postalCode,
    worker: {
      fullName: visitWorkerFullName(visit.status, visit.worker),
      phoneNumber: visit.worker?.contactNumber ?? null,
      avatarUrl: visit.worker?.avatarUrl ?? null,
      id: visit.worker?.id ?? "",
      workerRating: visit.worker?.workerRating ?? 0,
    },
    rating: visit.rating ?? 0,
    ratingComment: visit.ratingComment ?? "",
    reviewEditable: visit.reviewEditable,
    reviewAttachments:
      visit.reviewAttachments?.map((attachment) => ({
        id: attachment.id,
        url: attachment.downloadUrl,
      })) ?? [],
    reportDate: visit.reportDateIssue ? new Date(visit.reportDateIssue) : null,
    lineItems,
    token,
    clientCanReschedule: canReschedule ?? false,
    scheduleType: visit.job?.scheduleType as JobScheduleTypeEnum,
    packageProfile,
    packageId: pkg.id ?? null,
    modificationLink: visit.modificationLink ?? null,
    rebookAction: getFragmentData(RebookActionFieldsFragmentDoc, visit.rebookAction),
  };
}

export function VisitDetailContent({
  visitData,
  loading,
  handleReschedule,
  handleChangeWorker,
  handleReportVisit,
  handleSkipVisit,
  handleRateVisit,
  handleRebook,
}: Props) {
  const visits: VisitDetailData[] = visitData ? [visitData] : [];
  const pathname = usePathname();
  const closeVisitModal = useVisitDetailStore((state) => state.closeVisitModal);

  const handleContactProfessional = () => {
    if (visitData?.worker.phoneNumber) {
      handleWhatsappNumber(visitData.worker.phoneNumber);
    }
  };

  const onRecheduleWithProfessional = (id: string) => {
    onChatSales({
      path: pathname,
      includeTrackingUrl: true,
      message: `Hi! I would like to speak to sales about rescheduling my appointment ${id}`,
    });
  };

  const onSkipVisitWithProfessional = (id: string) => {
    onChatSales({
      path: pathname,
      includeTrackingUrl: true,
      message: `Hi! I would like to speak to sales about to skip my visit ${id}`,
    });
  };

  const lineItems = useMemo(
    () =>
      visitData?.lineItems.filter((item) => item.serviceType !== PackageServiceTypeEnum.Discount) ??
      [],
    [visitData?.lineItems],
  );

  const serviceDuration = useMemo(() => {
    const isDeepCleaning = lineItems[0]?.serviceType === PackageServiceTypeEnum.DeepCleaning;
    const { totalDuration, totalUnit } = (visitData?.lineItems ?? []).reduce(
      (totals, item) => {
        totals.totalDuration += item.duration;
        totals.totalUnit += item.unitsNumber;

        return totals;
      },
      { totalDuration: 0, totalUnit: 0 },
    );

    if (isDeepCleaning) {
      return `${formatDecimalHour(totalDuration)}`;
    } else {
      return `${formatDecimalHour(totalDuration)} (${totalUnit} Items)`;
    }
  }, [visitData?.lineItems]);

  const discounts = (visitData?.lineItems ?? []).filter(
    (item) => item.serviceType === PackageServiceTypeEnum.Discount,
  );

  const price = singaporeTaxCalculation(visitData?.value ?? 0);

  const renderActionButton = useMemo(() => {
    const horizontalReportButton = (
      <View
        className="flex cursor-pointer flex-row items-center justify-between gap-3"
        onClick={() => visitData && handleReportVisit(visitData.id)}
      >
        <WarningCircle size={24} className="color-foreground" />
        <Typography variant="body-sm" color="foreground-default" className="flex-1">
          Report Issue
        </Typography>
        <CaretRight size={16} className="color-foreground-intermediate" />
      </View>
    );

    if (!visitData) {
      return null;
    }

    if (visitData.status === VisitStatusEnum.Completed) {
      const serviceReportCard = loading ? null : (
        <ServiceReportCard
          visitId={visitData.id}
          visitToken={visitData.token}
          onOpen={closeVisitModal}
        />
      );

      // Only show if serviceDate is less than 2 weeks ago from now
      const now = new Date();
      const serviceDate = new Date(visitData.serviceDate);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      if (serviceDate >= twoWeeksAgo) {
        return (
          <View className="flex flex-col gap-4">
            <View className="gap-spacing-sm flex flex-col">
              <VisitRating
                variant="visitDetail"
                rating={visitData.rating}
                onClick={(rate) => handleRateVisit(visitData.id, rate)}
              />
              {serviceReportCard}
            </View>
            {handleRebook && visitShowsRebookButton(visitData) && (
              <Button
                variant="primary"
                color="CTA"
                size="md"
                onClick={() => handleRebook(visitData)}
                children="Rebook"
              />
            )}
            {horizontalReportButton}
          </View>
        );
      }
      return serviceReportCard;
    }

    if (visitData.status === VisitStatusEnum.PendingClientSchedule) {
      return horizontalReportButton;
    }

    if (visitData.status === VisitStatusEnum.Scheduled) {
      return (
        <View className="flex flex-row">
          <IfElse
            if={
              OnlineBookingDepartments.includes(visitData?.department) &&
              !OfflineBookingServiceTypes.includes(visitData?.serviceType)
            }
            else={
              <ActionButton
                label="Reschedule"
                iconName="whatsappLogo"
                onClick={() => onRecheduleWithProfessional(visitData.id)}
                variant="regular"
              />
            }
          >
            <ActionButton
              label="Reschedule"
              iconName="calendarDots"
              onClick={() => handleReschedule(visitData.id)}
              variant="regular"
            />
          </IfElse>

          <IfElse
            if={
              lineItems[0]?.serviceType !==
                (PackageServiceTypeEnum.DeepCleaning && PackageServiceTypeEnum.ElderCare) &&
              lineItems[0].department !== PackageDepartmentEnum.PetCare
            }
          >
            <ActionButton
              label={`Change ${getWorkerLabel(visitData.department)}`}
              iconName="userSwitch"
              onClick={() => handleChangeWorker(visitData.id)}
              variant="regular"
            />
          </IfElse>

          <ActionButton
            label="Report Issue"
            iconName="warningCircle"
            onClick={() => visitData && handleReportVisit(visitData.id)}
          />

          <IfElse
            if={lineItems[0]?.serviceType !== PackageServiceTypeEnum.DeepCleaning}
            else={
              <ActionButton
                label="Skip Visit"
                iconName="whatsappLogo"
                onClick={() => onSkipVisitWithProfessional(visitData.id)}
                variant="danger"
              />
            }
          >
            <ActionButton
              label="Skip Visit"
              iconName="xCircle"
              onClick={() => handleSkipVisit(visitData.id)}
              variant="danger"
            />
          </IfElse>
        </View>
      );
    }

    return horizontalReportButton;
  }, [visitData, loading, lineItems, closeVisitModal]);

  const serviceTitle = useMemo(() => {
    if (!visitData?.serviceType || !visitData?.department) return "";
    return packageServiceTypeLabel(visitData?.serviceType || "", visitData?.department || "");
  }, [visitData?.serviceType, visitData?.department]);

  return (
    <View className="flex flex-col gap-4">
      <View className="flex flex-row items-center justify-between md:mt-2">
        {!visitData || loading ? (
          <Skeleton className="h-4 w-[200px]" />
        ) : (
          <Typography variant="label-md">{serviceTitle}</Typography>
        )}
        {visitData && <VisitStatusBadge status={visitData.status} />}
      </View>
      <Divider />
      <View className="flex flex-col gap-2">
        {loading ? (
          <>
            <Skeleton className="h-2 w-[30%]" />
            <Skeleton className="h-2 w-[70%]" />
            <Skeleton className="h-2 w-[70%]" />
          </>
        ) : (
          <>
            <View className="flex flex-row items-center gap-1">
              <Typography variant="body-sm" color="foreground-intermediate">
                ID {visitData?.id ?? ""}
              </Typography>
              <IconButton
                variant="ghost"
                color="foreground"
                size="sm"
                className="p-0"
                iconName="copy"
                disabled={!visitData}
                onClick={() => visitData && copyClipboard(`Visit #${visitData.id}`)}
              />
            </View>
            <View className="flex flex-row items-center justify-between gap-1">
              <Typography variant="body-sm" color="foreground-intermediate">
                {visitData
                  ? formatVisitSchedule(
                      visitData.startTime,
                      visitData.endTime,
                      visitData.serviceDate,
                    )
                  : "Pending schedule"}
              </Typography>
              {visitData?.status === VisitStatusEnum.Scheduled ? (
                <AddToCalendarButton
                  title={serviceTitle}
                  description=""
                  location={visitData?.address || ""}
                  visits={visits}
                />
              ) : visitData?.status &&
                isVisitStatusPending(visitData?.status) &&
                !isReadyToSchedule(visitData?.serviceDate) ? (
                <>
                  <Typography variant={"body-sm"} color="foreground-attenuated">
                    |{" "}
                  </Typography>
                  <Typography
                    variant={"body-sm"}
                    color="warning"
                    className="mr-auto whitespace-nowrap"
                  >
                    Reschedule opens {formatDate(addMonths(visitData?.serviceDate, -1), "MMM dd")}
                  </Typography>
                </>
              ) : null}
            </View>
            <Typography variant="body-sm" color="foreground-intermediate">
              {visitData?.address ?? "Not available"}
            </Typography>
          </>
        )}
      </View>
      <Divider />
      <View className="flex flex-row items-center justify-between">
        <WorkerAvatar
          fullName={visitData?.worker.fullName ?? ""}
          avatarUrl={visitData?.worker.avatarUrl ?? null}
          loading={loading}
        />
        {visitData?.status === VisitStatusEnum.Scheduled && visitData?.worker.phoneNumber && (
          <Button
            variant="secondary"
            color="success"
            onClick={handleContactProfessional}
            iconName="whatsappIcon"
            iconColor="fill-success"
            children="Contact Professional"
            size="md"
            iconSize="md"
          />
        )}
      </View>
      {renderActionButton ? (
        <>
          <Divider />
          {renderActionButton}
        </>
      ) : null}
      {!loading && visitData?.packageProfile && (
        <>
          <Divider />
          <View className="flex flex-col gap-1">
            <ListHeading
              icon={ClipboardText}
              title={`${serviceProfileMapping(visitData.packageProfile.type)} Profile`}
            />
            <ServiceProfileCard profile={visitData.packageProfile} />
          </View>
        </>
      )}
      <Divider />
      <View className="flex flex-col gap-1">
        <ListHeading icon={Receipt} title="Your Order" />
        {loading ? <Skeleton className="h-10 w-full" /> : <LineItemList lineItems={lineItems} />}
      </View>
      <Divider />
      <View className="flex flex-col gap-1">
        {loading ? (
          <View className="flex flex-col gap-1">
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-3 w-1/4" />
          </View>
        ) : (
          <>
            <ListItem label="Service Duration/Visit" value={serviceDuration} />
            <ListItem label="Total Cost" value={formatPrice(visitData?.value ?? 0, 0)} />
            {discounts.map((discount, index) => (
              <ListItem
                key={discount.packageDetailId + index}
                label={discount.description}
                value={formatPrice(discount.billingValue)}
              />
            ))}
            <ListItem label={`GST ${SINGAPORE_TAX * 100}%`} value={formatPrice(price.tax)} />
          </>
        )}
      </View>
      <Divider />
      <View className="flex flex-row gap-1">
        <Typography variant="label-md" className="flex-1 px-0">
          Total
        </Typography>
        <View className="flex flex-row items-center">
          <Typography variant="label-md" className="color-brand-primary font-medium">
            {formatPrice(price.price)}{" "}
          </Typography>
          <Typography variant="body-sm" color="foreground-intermediate" as="span">
            incl. GST
          </Typography>
        </View>
      </View>
    </View>
  );
}

export function ListItem({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex flex-row gap-1">
      <Typography variant="body-sm" color="foreground-intermediate" className="flex-1 px-0">
        {label}
      </Typography>
      <Typography variant="body-sm">{value}</Typography>
    </View>
  );
}
